/**
 * ChatControls Component
 * Action buttons for chat management (history, export, clear, simulation toggle)
 */

import React from 'react';
import { FiClock, FiDownload, FiTrash2, FiZap, FiX } from 'react-icons/fi';

interface ChatControlsProps {
  onLoadHistory: () => void;
  onExport: () => void;
  onClear: () => void;
  onToggleSimulation: () => void;
  hasMessages: boolean;
  hasConversation: boolean;
  isSimulating: boolean;
  showSimulation: boolean;
  className?: string;
}

export const ChatControls: React.FC<ChatControlsProps> = ({
  onLoadHistory,
  onExport,
  onClear,
  onToggleSimulation,
  hasMessages,
  hasConversation,
  isSimulating,
  showSimulation,
  className = ''
}) => {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Load History */}
      <button
        onClick={onLoadHistory}
        disabled={!hasConversation}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
          hasConversation
            ? 'bg-slate-700 hover:bg-slate-600 text-gray-300'
            : 'bg-slate-800 text-gray-600 cursor-not-allowed'
        }`}
        title="Load conversation history"
      >
        <FiClock size={16} />
        <span className="text-sm">History</span>
      </button>

      {/* Export */}
      <button
        onClick={onExport}
        disabled={!hasMessages}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
          hasMessages
            ? 'bg-slate-700 hover:bg-slate-600 text-gray-300'
            : 'bg-slate-800 text-gray-600 cursor-not-allowed'
        }`}
        title="Export conversation"
      >
        <FiDownload size={16} />
        <span className="text-sm">Export</span>
      </button>

      {/* Clear */}
      <button
        onClick={onClear}
        disabled={!hasMessages}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
          hasMessages
            ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
            : 'bg-slate-800 text-gray-600 cursor-not-allowed'
        }`}
        title="Clear conversation"
      >
        <FiTrash2 size={16} />
        <span className="text-sm">Clear</span>
      </button>

      {/* Simulation Toggle */}
      <button
        onClick={onToggleSimulation}
        disabled={isSimulating}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
          showSimulation
            ? 'bg-purple-500 hover:bg-purple-600 text-white'
            : 'bg-slate-700 hover:bg-slate-600 text-gray-300'
        } ${isSimulating ? 'opacity-50 cursor-not-allowed' : ''}`}
        title={showSimulation ? 'Hide simulation panel' : 'Show simulation panel'}
      >
        {showSimulation ? <FiX size={16} /> : <FiZap size={16} />}
        <span className="text-sm">Simulate</span>
      </button>
    </div>
  );
};

export default ChatControls;
